import { Box, styled, Typography } from '@mui/material';

export const DialogColumns = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(320px, 1fr) minmax(300px, 1fr)',
  gap: theme.spacing(3),
  // take the available width (also inside the roster's employee-card popup),
  // but keep a stable footprint so the centred dialog does not jump while the
  // data loads
  width: 'min(1100px, calc(100vw - 80px))',
  // a fixed height, not a minimum: unfolding the working-days box or an error
  // message must not resize the dialog under the planner's cursor. Columns
  // scroll inside instead.
  height: 'min(560px, calc(100vh - 220px))',
  // stretch, so the right column can fill the height of the left one and give
  // the consequences panel every pixel that is left over
  alignItems: 'stretch',
  // narrow viewports: stack the columns instead of squeezing them
  [theme.breakpoints.down('medium')]: {
    gridTemplateColumns: '1fr',
    width: 'calc(100vw - 48px)',
    height: 'auto'
  }
}));

export const Column = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  minHeight: 0,
  // lets the content shrink inside the grid column instead of overflowing it
  minWidth: 0
}));

/** section heading in the app's idiom: green title with a thin rule below */
export const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  paddingBottom: theme.spacing(0.5),
  // room for the floating labels of the fields right below the rule
  marginBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.primary.main}`
}));

export const SectionPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius
}));

export const PeriodRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

export const DayCount = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  whiteSpace: 'nowrap',
  // never squeezed away: the duration is the check on the chosen period
  flexShrink: 0
}));

export const NoticeBanner = styled(Box)(({ theme }) => ({
  background: theme.palette.info.light,
  color: theme.palette.getContrastText(theme.palette.info.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  marginBottom: theme.spacing(2),
  fontSize: 13
}));

export const ConsequencesBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  // fills the panel: as many rows visible as the dialog height allows
  flex: 1,
  minHeight: 180,
  overflowY: 'auto'
}));

/**
 * columns: work agreement | day+date | time | duty | +/− | open duty
 * marker. Every track is fixed or resolves identically per row (equal fixed
 * tracks, equal fr distribution), so the rows line up as real columns
 * instead of shifting with the width of their content.
 */
const CONSEQUENCE_COLUMNS =
  'minmax(90px, 1.1fr) 72px 96px minmax(90px, 1fr) 28px 32px';

/**
 * duties of the same day share a background (the app's zebra colour), so the
 * borders between rows can stay away inside a group: the day boundary is the
 * colour change
 */
export const ConsequenceRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'shaded'
})<{ shaded?: boolean }>(({ theme, shaded }) => ({
  display: 'grid',
  gridTemplateColumns: CONSEQUENCE_COLUMNS,
  alignItems: 'center',
  columnGap: theme.spacing(1),
  padding: theme.spacing(0, 1),
  // fixed height: rows with an icon or mark stay exactly as tall as rows
  // with only text
  height: 34,
  fontSize: 13,
  backgroundColor: shaded ? theme.palette.action.hover : undefined
}));

/** column labels above the rows, in the table-header idiom */
export const ConsequenceHeaderRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: CONSEQUENCE_COLUMNS,
  columnGap: theme.spacing(1),
  padding: theme.spacing(0, 1, 0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
}));

export const ConsequenceCell = styled(Box)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

export const WorkAgreementMeta = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13
}));

/** a colour per work agreement, so tab and description belong together */
export const WORK_AGREEMENT_COLORS = [
  'primary.main',
  'info.main',
  'secondary.main',
  'warning.dark'
];

export const HintText = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  marginBottom: theme.spacing(1)
}));

export const FteRolloutBox = styled(Box)(({ theme }) => ({
  border: `1px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(1)
}));

export const InfoBox = styled(Box)(({ theme }) => ({
  background: theme.palette.info.light,
  color: theme.palette.getContrastText(theme.palette.info.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  fontSize: 13
}));

export const WarningBox = styled(Box)(({ theme }) => ({
  background: theme.palette.warning.light,
  color: theme.palette.getContrastText(theme.palette.warning.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  fontSize: 13
}));

/** for situations where continuing is pointless, e.g. an overlapping absence */
export const ErrorBox = styled(Box)(({ theme }) => ({
  background: theme.palette.error.light,
  color: theme.palette.getContrastText(theme.palette.error.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  fontSize: 13
}));
